import {
  useState,
  ChangeEvent,
  RefObject,
  FocusEvent,
  useRef,
  useEffect,
} from 'react';
import { useRouter } from 'next/router';
import { COOKIES_CONFIG, PATHS, noop } from '@belong/common';
import { Button, Form, TextField } from '@belong/ui';
import clsx from 'clsx';
import { parseCookies } from 'nookies';
import type {
  AddressModel,
  AddressRequestModel,
  PropertyOnboardingModel,
} from 'src/api/models';
import { trackEventService } from 'src/api/tracking';
import { AddressAutocomplete } from 'src/components/address-autocomplete/address-autocomplete';
import { SignUpSignInModal } from 'src/features/auth/components/sign-up-sign-in-modal/sign-up-sign-in-modal';
import { useReddit } from 'src/features/document/hooks';
import { QualifyModal } from 'src/features/homeowners/components/qualify-modal/qualify-modal';
import { HOMEOWNERS_STRINGS } from 'src/features/homeowners/homeowners.strings';
import { useCheckCoverage } from 'src/features/homeowners/hooks/use-check-coverage';
import { useCreateProperties } from 'src/features/homeowners/queries';
import { getHomeownerPropertyByAddress } from 'src/features/homeowners/utils';
import {
  GA_ENTRY_POINTS,
  GA_EVENTS,
  GA_EXPERIMENT,
  QUERY_PARAMS,
} from 'src/features/rent-estimate/constants';
import { useUser, useMutateUser } from 'src/features/users/useUser';
import { useModal } from 'src/hooks/useModal';
import { HomeAlreadyExistsModal } from 'src/modals/home-already-exists-modal/home-already-exists-modal';
import { NotThereYet } from 'src/modals/not-there-yet/not-there-yet';
import { useStore } from 'src/store';
import { setAddressSelector } from 'src/store/calculator';
import { replaceModalSelector } from 'src/store/modal';
import { ApiError } from 'src/utils/build-api-client';
import {
  GA_ACTIONS,
  setEnhancedConvertionData,
  trackHomeownerAddressEntered,
} from 'src/utils/tracking';
import css from './address-bar.module.css';

/**
 * What the address bar's single field holds: an `AddressModel` plus `address`,
 * the formatted string `AddressAutocomplete` writes back into the field.
 */
type FormAddress = AddressModel & { value: string; address?: string };
type FormValues = {
  address: FormAddress;
  unit: string;
};

type AddressBarProps = {
  variant?: 'primary' | 'secondary' | 'filled';
  ctaRef?: RefObject<HTMLDivElement>;
};

export function AddressBar({
  ctaRef,
  variant = 'primary',
}: AddressBarProps): JSX.Element {
  const router = useRouter();
  const { user } = useUser();
  const { mutateAsync: fetchUser } = useMutateUser();
  const { userId } = user || {};
  const [isAddressFieldDirty, setIsAddressFieldDirty] = useState(false);
  const [areCTAsDisabled, setAreCTAsDisabled] = useState(false);
  const [addressValue, setAddressValue] = useState<FormAddress | null>(null);
  const [unitFieldValue, setUnitFieldValue] = useState(''); // TODO: doing this the easy way, we can improve it later
  const [isQualifyModalOpen, openQualifyModal, closeQualifyModal] =
    useModal(false);
  const replaceModal = useStore(replaceModalSelector);
  const setAddress = useStore(setAddressSelector);
  const { isCreatePropertiesLoading, createProperties } = useCreateProperties();
  const [isModalOpen, openModal, closeModal] = useModal(false);
  const [isNotThereYetModalOpen, openNotThereYetModal, closeNotThereYetModal] =
    useModal(false);
  const { checkCoverage, property: notThereYetProperty } =
    useCheckCoverage(false);
  const cookies = parseCookies();
  const [
    isSignUpSignInModalOpen,
    openSignUpSignInModal,
    closeSignUpSignInModal,
  ] = useModal();
  const selectedUnitNumber = useRef<string>();
  const isHomeAlreadyClaimed = useRef(false);
  const property = useRef<PropertyOnboardingModel>();
  const [initialValues, setInitialValues] = useState<FormValues>({
    // Cast: the field is seeded with only the parts the user types, and
    // `AddressAutocomplete` replaces the whole object on select.
    address: {
      value: '',
      streetAddress: '',
      city: '',
      state: '',
      zipcode: '',
    } as FormAddress,
    unit: '',
  });

  const reddit = useReddit();

  const referrerUniqueId = cookies[COOKIES_CONFIG.REFERRAL_ID.name];

  function handleAddressValueChange(address: string) {
    setIsAddressFieldDirty(address !== '');

    if (address === '') {
      setAddressValue(null);
      localStorage.setItem('address-from-next', '');
      localStorage.setItem('address-from-onboarding', '');
    }
  }

  async function handleCalculatorClick() {
    try {
      setAreCTAsDisabled(true);

      if (!addressValue) {
        /**
         * @todo Check if this is still necessary
         */
        await trackEventService({
          eventName: GA_EVENTS.FLOW_STARTED,
          metadata: {
            ...(user?.userId && { userId: user.userId }),
            variant: GA_ENTRY_POINTS.HO_ADDRESS_INPUT,
            experiment: GA_EXPERIMENT.RENT_ESTIMATE,
          },
        });

        router.push(PATHS.RENT_ESTIMATE_FLOW);

        return;
      }

      const isInCoverage = await checkCoverage([addressValue]);

      if (!isInCoverage) {
        if (!userId) {
          openQualifyModal();
        } else {
          await handlePropertyCreation(addressValue, true);

          openNotThereYetModal();
        }

        return setAreCTAsDisabled(false);
      }

      if (unitFieldValue) {
        addressValue.unitNumber = unitFieldValue;
      }

      setAddressValue(addressValue);

      trackHomeownerAddressEntered(
        GA_ACTIONS.ADDRESS_ENTERED_CALCULATOR,
        addressValue,
      );

      await trackEventService({
        eventName: GA_EVENTS.FLOW_STARTED,
        metadata: {
          ...(user?.userId && { userId: user.userId }),
          variant: GA_ENTRY_POINTS.HO_ADDRESS_INPUT,
          experiment: GA_EXPERIMENT.RENT_ESTIMATE,
        },
      });

      router.push({
        pathname: PATHS.RENT_ESTIMATE_FLOW,
        query: {
          [QUERY_PARAMS.ADDRESS]: JSON.stringify(addressValue),
        },
      });
    } finally {
      setAreCTAsDisabled(false);
    }
  }

  // TODO: Refactor this, added second argument because it was the easy way and had to push
  async function handlePropertyCreation(
    addressParam?: FormAddress,
    outOfCoverage?: boolean,
  ) {
    const address = addressParam || addressValue;
    if (!address) return;

    let units = [{}];
    if (address.unitNumber) {
      units = [{ unitNumber: address.unitNumber }];
    }

    // Cast: `NewPropertyModel` wants an `AddressRequestModel`, whose street /
    // city / state / zipcode are required — the field carries the optional
    // `AddressModel` shape, and the submit paths only get here with them set.
    const properties = [{ address: address as AddressRequestModel, units }];
    try {
      await createProperties({
        properties,
        creationSource: 'SignUpFlow',
        referrerUniqueId,
      });
      if (outOfCoverage) return;

      router.push(`/homeowner/onboard?mode=resume`);
    } catch (err) {
      console.error(err);
      if (err instanceof ApiError && err.code === 'HOME_ALREADY_CLAIMED') {
        return openModal();
      }
      handleClose();
    }
  }

  function handleQualifyModalLogin() {
    replaceModal({
      type: 'NOT_THERE_YET',
      props: { property: notThereYetProperty, source: 'SignUpFlow' },
    });
  }

  async function createProperty({
    isOutOfCoverage,
  }: {
    isOutOfCoverage: boolean;
  }) {
    try {
      let units = [{}];

      if (addressValue?.unitNumber) {
        units = [{ unitNumber: addressValue.unitNumber }];
      }

      const properties = [
        { address: addressValue as AddressRequestModel, units },
      ];

      const { properties: createdProperties } = await createProperties({
        properties,
        creationSource: 'SignUpFlow',
        referrerUniqueId,
      });

      isHomeAlreadyClaimed.current = false;

      if (isOutOfCoverage) {
        return openNotThereYetModal();
      }

      const createdProperty = getHomeownerPropertyByAddress({
        address: addressValue as AddressModel,
        homeownerProperties: createdProperties!,
      });

      property.current = createdProperty;
    } catch (error) {
      if (error instanceof ApiError && error.code === 'HOME_ALREADY_CLAIMED') {
        isHomeAlreadyClaimed.current = true;

        openModal();
      }
    }
  }

  async function handleSeeIfYouQualifyClick() {
    try {
      setAreCTAsDisabled(true);

      reddit.track('ViewContent');

      // Adds validation for proper street address
      if (
        addressValue &&
        (!addressValue?.streetAddress ||
          addressValue.streetAddress.trim() === '')
      ) {
        // Fall back to using formatted address if no street address found
        // `!`: this branch is the "no street address" fallback and already
        // read the formatted string unconditionally.
        const formattedParts = addressValue.address!.split(',');

        if (formattedParts.length > 0) {
          addressValue.streetAddress = formattedParts[0]!.trim();
        }
      }

      if (
        addressValue?.streetAddress &&
        addressValue.streetAddress.length > 1
      ) {
        if (unitFieldValue) {
          addressValue.unitNumber = unitFieldValue;
        }

        setAddressValue(addressValue);

        const isInCoverage = await checkCoverage([addressValue]);

        if (!userId) {
          setAddress(addressValue);

          return openSignUpSignInModal();
        }

        if (!isInCoverage) {
          return createProperty({ isOutOfCoverage: true });
        }

        await createProperty({ isOutOfCoverage: false });

        if (!isHomeAlreadyClaimed.current) {
          setAddress(addressValue);

          return router.push(
            `/homeowners/sign-up/${property.current?.units?.[0]?.basicInfo?.unitId}`,
          );
        }
      } else {
        // In case there is an addressValue with empty streetAdress or a region left from before
        localStorage.setItem(
          'address-from-next',
          JSON.stringify(addressValue || {}),
        );
        router.push(PATHS.HOMEOWNER_ONBOARDING_PROPERTY_ADDRESS);
      }
    } finally {
      setAreCTAsDisabled(false);
    }
  }

  function handleClose() {
    closeQualifyModal();
    closeNotThereYetModal();

    closeSignUpSignInModal();
    closeModal();

    delete router.query.step;

    router.push(router, undefined, { shallow: true });

    localStorage.setItem('address-from-next', '');
  }

  function handleAddressSelect(address: FormAddress) {
    if (address.streetAddress) {
      // Removes any leading or trailing whitespace from the street address to ensure consistent formatting
      address.streetAddress = address.streetAddress.trim();
    }

    address.unitNumber = selectedUnitNumber.current;

    setAddressValue(address);
    setEnhancedConvertionData(address);
  }

  function onUnitBlur(event: FocusEvent<HTMLInputElement>) {
    setIsUnitFocused(false);
    const unitNumber = event.target.value?.trim();

    selectedUnitNumber.current = unitNumber;
    if (unitNumber === '') {
      // Avoid send empty string as a unitNumber (unitNumber='')
      selectedUnitNumber.current = undefined;
    }
  }

  async function handleAuthenticationSuccess() {
    /**
     * This call is necessary to actually show the authenticated user on the header or upon navigation.
     * It is probably related to the "MY ACCOUNT" item being in a layout that is preserved across navigations.
     * A good solution, without doing a major refactor would be to just embed the header in all the pages.
     * That way it will always be recreated across navigations and the authenticated user will be shown in all scenarios.
     */
    const freshUser = await fetchUser();

    window.geq?.suppress(freshUser?.email);

    await createProperty({ isOutOfCoverage: Boolean(notThereYetProperty) });

    if (notThereYetProperty) {
      return;
    }

    if (!isHomeAlreadyClaimed.current) {
      router.push(
        `/homeowners/sign-up/${property.current?.units?.[0]?.basicInfo?.unitId}`,
      );
    }
  }

  function handleSignUpSignInModalDismiss() {
    closeSignUpSignInModal();
  }

  useEffect(() => {
    const addressFromOnboarding = localStorage.getItem(
      'address-from-onboarding',
    );

    if (addressFromOnboarding) {
      const address = JSON.parse(addressFromOnboarding);

      setInitialValues({ address, unit: '' });
      setAddressValue(address);
    }
  }, []);

  const [isAddressFocused, setIsAddressFocused] = useState(false);
  const [isUnitFocused, setIsUnitFocused] = useState(false);
  const isAnyInputFocused = isAddressFocused || isUnitFocused;

  return (
    <>
      <Form initialValues={initialValues} enableReinitialize onSubmit={noop}>
        {() => {
          return (
            <div className="mb-xl flex w-full flex-col lg:flex-row">
              <div
                className={clsx('w-full grow', {
                  'mb-lg lg:mb-0': isAddressFieldDirty && variant !== 'filled',
                  'lg:mr-lg transition-all duration-hover':
                    variant !== 'filled',
                  // `!`: CSS-module classes are `string | undefined` under the
                  // app's index-signature declaration.
                  [css.addressRedirectSmall!]: isAddressFieldDirty,
                  [css.addressGlow!]: variant === 'filled',
                  [css.addressGlowDone!]: isAddressFieldDirty,
                })}
              >
                <AddressAutocomplete
                  id="address-bar-address"
                  name="address"
                  label={HOMEOWNERS_STRINGS['cover.addressPlaceholder']}
                  onAdressSelect={handleAddressSelect}
                  onValueChange={handleAddressValueChange}
                  variant={variant}
                  className={clsx(
                    isAddressFieldDirty &&
                      variant !== 'filled' &&
                      'lg:rounded-r-none lg:border-r-0',
                    variant === 'filled' &&
                      clsx(
                        'transition-colors duration-hover focus:border-white',
                        isAddressFieldDirty
                          ? 'border-white'
                          : 'border-white/20',
                      ),
                  )}
                  textInputProps={{
                    onFocus: () => setIsAddressFocused(true),
                    onBlur: () => setIsAddressFocused(false),
                  }}
                  focused={isAnyInputFocused}
                />
              </div>
              <div
                className={clsx(
                  'w-full lg:w-0',
                  css.unitField,
                  variant === 'filled'
                    ? // Kept in place and grown open: wider on large screens,
                      clsx(
                        'overflow-hidden transition-[width,max-height,margin,opacity] duration-hover ease-out motion-reduce:transition-none lg:shrink-0',
                        isAddressFieldDirty
                          ? clsx(
                              'mt-2sm max-h-[200px] opacity-100 lg:ml-xs lg:mt-0',
                              css.unitFieldVisible,
                            )
                          : 'pointer-events-none mt-0 max-h-0 opacity-0',
                      )
                    : clsx(
                        'transition-all duration-hover',
                        isAddressFieldDirty
                          ? clsx('block', css.unitFieldVisible)
                          : 'pointer-events-none hidden h-0',
                      ),
                )}
                aria-hidden={!isAddressFieldDirty || undefined}
              >
                <div
                  className={variant === 'filled' ? 'lg:w-[160px]' : undefined}
                >
                  <TextField
                    id="address-bar-unit"
                    name="unit"
                    variant={variant}
                    label={HOMEOWNERS_STRINGS['cover.unitPlaceholder']}
                    value={unitFieldValue}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setUnitFieldValue(event.target.value)
                    }
                    onBlur={onUnitBlur}
                    className={clsx(
                      isAddressFieldDirty &&
                        variant !== 'filled' &&
                        'lg:rounded-l-none',
                      variant === 'filled' &&
                        clsx(
                          'transition-colors duration-hover focus:border-white',
                          unitFieldValue ? 'border-white' : 'border-white/20',
                        ),
                    )}
                    onFocus={() => setIsUnitFocused(true)}
                    focused={isAnyInputFocused}
                    tabIndex={isAddressFieldDirty ? undefined : -1}
                  />
                </div>
              </div>
            </div>
          );
        }}
      </Form>
      <div
        ref={ctaRef}
        className="gap-x-2sm md:gap-x-sm grid w-full grid-cols-2 lg:w-auto"
      >
        <Button
          size={{ _: 'fluid', lg: 'default' }}
          variant="outline"
          buttonType="secondary"
          onClick={handleCalculatorClick}
          className={clsx(
            css.buttonShadow,
            // customize disabled button styles (we might want to move this to the Button component)
            'disabled:border-green disabled:text-green disabled:bg-transparent disabled:opacity-50',
          )}
          disabled={areCTAsDisabled}
        >
          {HOMEOWNERS_STRINGS['doTheMath.cta']}
        </Button>
        <Button
          className={clsx(
            css.buttonShadow,
            'disabled:bg-green disabled:opacity-50',
          )}
          onClick={handleSeeIfYouQualifyClick}
          disabled={areCTAsDisabled}
        >
          {HOMEOWNERS_STRINGS['cover.ctaLabel']}
        </Button>
      </div>

      <QualifyModal
        onDismiss={closeQualifyModal}
        isOpen={isQualifyModalOpen}
        onSuccessfulLogin={handleQualifyModalLogin}
        isLoading={isCreatePropertiesLoading}
      />
      {notThereYetProperty && (
        <NotThereYet
          isOpen={isNotThereYetModalOpen}
          onDismiss={handleClose}
          property={notThereYetProperty}
        />
      )}
      <HomeAlreadyExistsModal
        isOpen={isModalOpen}
        onDismiss={handleClose}
        ctaText="Change Address"
        onCtaClick={handleClose}
      />
      {isSignUpSignInModalOpen && (
        <SignUpSignInModal
          initialActiveTab="sign-up"
          isOpen
          onDismiss={handleSignUpSignInModalDismiss}
          onSignInSuccess={handleAuthenticationSuccess}
          onSignUpSuccess={handleAuthenticationSuccess}
        />
      )}
    </>
  );
}
